import { CSV_TARGETS, IDENT_KEYS, type ParsedCsv } from "../../lib/csv";
import {
  buildPointResultRows,
  type PointResultRow,
} from "../../lib/resultProperties";
import {
  buildZoneResultRows,
  csvIds,
  pointLayerIdMatch,
  resolveZoneCsvIds,
  strictIdMatch,
  type IdMatchVerdict,
  type ZoneIdIndex,
} from "../../lib/targetResultsMatch";

const label = (key: string) =>
  CSV_TARGETS.find((target) => target.key === key)?.label ?? key;

export type PreviewAnalysis = {
  mapping: Record<string, number | null>;
  ids: string[] | null;
  analytes: string[];
  verdict: IdMatchVerdict | null;
  zoneRows: ReturnType<typeof buildZoneResultRows> | null;
  pointRows: {
    rows: PointResultRow[];
    valuelessIds: string[];
    blankIds: string[];
  } | null;
};

export function previewAnalysis(args: {
  parsed: ParsedCsv;
  mapping: Record<string, number | null>;
  targetIds: string[];
  zoneIdx: ZoneIdIndex | null;
  kind: "points" | "zones";
}): PreviewAnalysis {
  const ids = csvIds(args.parsed, args.mapping);
  const analytes = Object.entries(args.mapping)
    .filter(([key, column]) => column != null && !IDENT_KEYS.has(key))
    .map(([key]) => label(key));
  const compared =
    ids && args.zoneIdx ? resolveZoneCsvIds(ids, args.zoneIdx) : ids;
  const verdict: IdMatchVerdict | null = compared
    ? args.kind === "points"
      ? pointLayerIdMatch(args.targetIds, compared)
      : strictIdMatch(args.targetIds, compared)
    : null;
  return {
    mapping: args.mapping,
    ids,
    analytes,
    verdict,
    zoneRows: args.zoneIdx
      ? buildZoneResultRows(args.parsed, args.mapping, args.zoneIdx)
      : null,
    pointRows:
      args.kind === "points"
        ? buildPointResultRows(args.parsed, args.mapping)
        : null,
  };
}
